import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const EMPTY_FORM = {
  type: 'client',
  entityId: '',
  staffUsers: [],
};

const getStaffLabel = (user = {}) => {
  const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim();
  return fullName || user.email || 'Staff';
};

const getEntityId = (entity = {}) => String(entity._id || '');

const normalizePortfolio = (portfolio = []) =>
  (Array.isArray(portfolio) ? portfolio : [])
    .map((entry, index) => ({
      user: entry.user?._id || entry.user || entry.userId,
      userDetails: entry.user,
      priority: Number(entry.priority || index + 1),
    }))
    .filter((entry) => entry.user)
    .sort((a, b) => a.priority - b.priority)
    .map((entry, index) => ({ ...entry, priority: index + 1 }));

const hasPortfolio = (entity = {}) => normalizePortfolio(entity.staffPortfolio).length > 0;

function PortfolioChips({ portfolio }) {
  const rows = normalizePortfolio(portfolio);
  if (!rows.length) return <Typography variant="body2" color="text.secondary">Aucun</Typography>;

  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {rows.map((entry) => (
        <Chip
          key={`${entry.user}-${entry.priority}`}
          size="small"
          label={`${entry.priority}. ${getStaffLabel(entry.userDetails || {})}`}
        />
      ))}
    </Stack>
  );
}

function PortfolioTable({ title, caption, rows, type, onCreate, onEdit, onClear }) {
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{caption}</Typography>
        </Box>
        <Button startIcon={<AddIcon />} variant="contained" onClick={() => onCreate(type)}>
          Ajouter
        </Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Gestionnaires</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={`${type}-${row.entity._id}`}>
                <TableCell>{row.entity.name || 'Sans nom'}</TableCell>
                <TableCell>{row.organizationName || '—'}</TableCell>
                <TableCell><PortfolioChips portfolio={row.entity.staffPortfolio} /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => onEdit(type, row.entity)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Vider">
                    <IconButton size="small" color="error" onClick={() => onClear(type, row.entity)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={4}>Aucun rattachement configuré.</TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function StaffPortfolioPage() {
  const [summary, setSummary] = useState({ organizations: [], contracts: [] });
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const staffOptions = useMemo(
    () => staffUsers.map((user) => ({ ...user, value: String(user._id), label: getStaffLabel(user) })),
    [staffUsers]
  );

  const clientOptions = useMemo(
    () => (summary.organizations || [])
      .map((row) => row.entity)
      .filter(Boolean)
      .map((entity) => ({ ...entity, value: getEntityId(entity), label: entity.name || 'Sans nom' })),
    [summary.organizations]
  );

  const crtOptions = useMemo(
    () => (summary.contracts || [])
      .map((row) => ({
        ...row.entity,
        organizationName: row.organization?.name || row.entity?.Organization?.name || '',
      }))
      .filter((entity) => entity?._id)
      .map((entity) => ({
        ...entity,
        value: getEntityId(entity),
        label: entity.organizationName ? `${entity.name || 'Sans nom'} - ${entity.organizationName}` : entity.name || 'Sans nom',
      })),
    [summary.contracts]
  );

  const clientRows = useMemo(
    () => clientOptions
      .filter(hasPortfolio)
      .map((entity) => ({ entity, organizationName: '' })),
    [clientOptions]
  );

  const crtRows = useMemo(
    () => crtOptions
      .filter(hasPortfolio)
      .map((entity) => ({ entity, organizationName: entity.organizationName || '' })),
    [crtOptions]
  );

  const entityOptions = form.type === 'client' ? clientOptions : crtOptions;
  const selectedEntity = entityOptions.find((option) => option.value === form.entityId) || null;

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryResponse, staffResponse] = await Promise.all([
        api.get('/api/staff-portfolio/summary'),
        api.get('/api/staff-portfolio/staff-options'),
      ]);
      setSummary(summaryResponse.data || { organizations: [], contracts: [] });
      setStaffUsers(Array.isArray(staffResponse.data) ? staffResponse.data : []);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreate = (type) => {
    setForm({ ...EMPTY_FORM, type });
    setDialogOpen(true);
  };

  const openEdit = (type, entity) => {
    const selectedUsers = normalizePortfolio(entity.staffPortfolio)
      .map((entry) => staffOptions.find((option) => option.value === String(entry.user)))
      .filter(Boolean);
    setForm({
      type,
      entityId: getEntityId(entity),
      staffUsers: selectedUsers,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setForm(EMPTY_FORM);
  };

  const savePortfolio = async (override = null) => {
    const payload = override || form;
    if (!payload.entityId) {
      setError('Sélectionne un client ou un CRT.');
      return;
    }

    setSaving(true);
    try {
      const endpoint = payload.type === 'client'
        ? `/api/staff-portfolio/organizations/${payload.entityId}`
        : `/api/staff-portfolio/contracts/${payload.entityId}`;

      await api.put(endpoint, {
        staffPortfolio: payload.staffUsers.map((user, index) => ({
          user: user.value || user._id,
          priority: index + 1,
        })),
      });

      closeDialog();
      await loadData();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur d enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const clearPortfolio = (type, entity) => {
    savePortfolio({
      type,
      entityId: getEntityId(entity),
      staffUsers: [],
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Porte Feuille</Typography>
          <Typography variant="body2" color="text.secondary">
            Rattachement ordonné du staff aux clients et aux CRT.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}>
          Actualiser
        </Button>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <PortfolioTable
            title="Clients"
            caption="Portefeuille staff rattaché directement aux organisations clientes."
            rows={clientRows}
            type="client"
            onCreate={openCreate}
            onEdit={openEdit}
            onClear={clearPortfolio}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <PortfolioTable
            title="CRT"
            caption="Gestionnaires staff rattachés aux CRT / contrats."
            rows={crtRows}
            type="crt"
            onCreate={openCreate}
            onEdit={openEdit}
            onClear={clearPortfolio}
          />
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{form.type === 'client' ? 'Portefeuille client' : 'Portefeuille CRT'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              select
              size="small"
              label="Type"
              value={form.type}
              onChange={(event) => setForm({ ...EMPTY_FORM, type: event.target.value })}
              SelectProps={{ native: true }}
            >
              <option value="client">Client</option>
              <option value="crt">CRT</option>
            </TextField>

            <Autocomplete
              options={entityOptions}
              value={selectedEntity}
              getOptionLabel={(option) => option.label || option.name || 'Sans nom'}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_, value) => {
                const selectedUsers = normalizePortfolio(value?.staffPortfolio)
                  .map((entry) => staffOptions.find((option) => option.value === String(entry.user)))
                  .filter(Boolean);
                setForm({
                  ...form,
                  entityId: value?.value || '',
                  staffUsers: selectedUsers,
                });
              }}
              renderInput={(params) => (
                <TextField {...params} label={form.type === 'client' ? 'Client' : 'CRT'} size="small" />
              )}
            />

            <Autocomplete
              multiple
              options={staffOptions}
              value={form.staffUsers}
              getOptionLabel={(option) => option.label || getStaffLabel(option)}
              isOptionEqualToValue={(option, value) => String(option.value) === String(value.value)}
              onChange={(_, value) => setForm({ ...form, staffUsers: value })}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value}
                    size="small"
                    label={`${index + 1}. ${option.label}`}
                  />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Gestionnaires" size="small" />}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Annuler</Button>
          <Button variant="contained" onClick={() => savePortfolio()} disabled={saving}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
